import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Search, CircleAlert } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { DataTable, EmptyState, type Coluna } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { cpf as fCpf, data as fData, moeda } from "../lib/formato.js";
import { RACA_COR, GRAU_INSTRUCAO } from "../lib/br.js";
import { usePode } from "../shell/Sessao.js";

type Linha = Awaited<ReturnType<typeof trpc.cadastro.pessoas.query>>[number];

const ROTULO: Record<string, string> = {
  ativo: "Ativo", afastado: "Afastado", ferias: "Em férias",
  desligado: "Desligado", sem_vinculo: "Sem vínculo", admitido: "Admitido",
};
const COR: Record<string, string> = {
  ativo: "var(--sinal-bom)", afastado: "var(--sinal-atencao)",
  ferias: "var(--muted-foreground)", desligado: "var(--sinal-critico)",
  sem_vinculo: "var(--faint-foreground)", admitido: "var(--primary)",
};

export function QuadroDePessoas() {
  const [busca, setBusca] = useState("");
  const [incluirDesligados, setIncluirDesligados] = useState(false);
  const [cadastrando, setCadastrando] = useState(false);
  const podeVerSalario = usePode("folha.lerRemuneracao");
  const podeCadastrar = usePode("pessoa.escrever");
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["cadastro", "pessoas", busca, incluirDesligados],
    queryFn: () => trpc.cadastro.pessoas.query({ busca, incluirDesligados, limite: 500 }),
  });

  const colunas: Coluna<Linha>[] = [
    { chave: "nome", titulo: "Nome", valor: (l) => l.nome,
      render: (l) => (
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: COR[l.situacao] ?? "var(--faint-foreground)" }} />
          <span className="truncate">{l.nome}</span>
        </span>
      ) },
    { chave: "matricula", titulo: "Matrícula", valor: (l) => l.matricula ?? "",
      render: (l) => <span className="num">{l.matricula ?? "sem registro"}</span> },
    { chave: "cpf", titulo: "CPF", valor: (l) => l.cpf,
      render: (l) => <span className="num">{fCpf(l.cpf)}</span> },
    { chave: "cargo", titulo: "Cargo", valor: (l) => l.cargo ?? "",
      render: (l) => l.cargo ?? "sem registro" },
    { chave: "estabelecimento", titulo: "Local", valor: (l) => l.estabelecimento ?? "",
      render: (l) => l.estabelecimento ?? "sem registro" },
    { chave: "admissao", titulo: "Admissão", valor: (l) => l.admissao ?? "",
      render: (l) => l.admissao ? fData(l.admissao) : "sem registro" },
    // A coluna de salário não é renderizada mascarada: ela simplesmente não
    // existe para quem não tem escopo. Mascarar no cliente esconde um dado que
    // já saiu do banco.
    ...(podeVerSalario
      ? [{ chave: "salario", titulo: "Salário", numerico: true, total: true,
           valor: (l: Linha) => Number(l.salario ?? 0),
           render: (l: Linha) => l.salario ? moeda(l.salario) : "sem registro",
         } satisfies Coluna<Linha>]
      : []),
    { chave: "situacao", titulo: "Situação", valor: (l) => l.situacao,
      render: (l) => ROTULO[l.situacao] ?? l.situacao },
  ];

  return (
    <Pagina
      secao="Pessoas" titulo="Quadro de pessoas"
      carimbo={q.data ? `${q.data.length} ${q.data.length === 1 ? "pessoa" : "pessoas"}` : undefined}
      acoes={podeCadastrar ? (
        <button className="botao" data-variante="primario" onClick={() => setCadastrando(true)}>
          <UserPlus aria-hidden size={15} /> Nova pessoa
        </button>
      ) : undefined}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="eop-busca" style={{ maxWidth: "24rem" }}>
          <Search aria-hidden size={15} className="eop-busca-icone" />
          <input className="campo eop-busca-campo" value={busca}
                 placeholder="Nome, matrícula ou CPF"
                 aria-label="Buscar pessoa"
                 onChange={(e) => setBusca(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-xs"
               style={{ color: "var(--muted-foreground)" }}>
          <input type="checkbox" checked={incluirDesligados}
                 onChange={(e) => setIncluirDesligados(e.target.checked)} />
          incluir desligados
        </label>
      </div>

      {q.isPending && <Carregando linhas={6} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}
      {q.isSuccess && q.data.length === 0 && (
        <EmptyState
          titulo={busca ? "Ninguém com esse nome, matrícula ou CPF" : "A sua base ainda não tem pessoas"}
          descricao={busca
            ? "A busca ignora acento, então 'Jose' encontra 'José'. Confira o CPF sem pontuação."
            : "A importação valida CPF, PIS e os campos que toda planilha esquece: raça e cor, grau de instrução e nome da mãe. Sem eles o S-2200 é rejeitado em lote."}
          {...(podeCadastrar && !busca
            ? { acao: <button className="botao" data-variante="primario"
                              onClick={() => setCadastrando(true)}>Cadastrar a primeira</button> }
            : {})}
        />
      )}
      {q.isSuccess && q.data.length > 0 && (
        <Cartao titulo="Pessoas" subtitulo="O estado mostrado é o vigente hoje">
          <DataTable colunas={colunas} linhas={q.data}
                     chaveDe={(l) => l.id + (l.vinculoId ?? "")} rotulo="Quadro de pessoas" />
        </Cartao>
      )}

      {cadastrando && (
        <FormPessoa
          aoFechar={() => setCadastrando(false)}
          aoCriar={() => { setCadastrando(false); void qc.invalidateQueries({ queryKey: ["cadastro"] }); }}
        />
      )}
    </Pagina>
  );
}

function FormPessoa({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
  const [f, setF] = useState({
    cpf: "", nome: "", nomeSocial: "", dataNascimento: "",
    sexo: "F" as "F" | "M", racaCor: 6, grauInstrucao: 7,
    nomeMae: "", nisPisPasep: "",
  });
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: () => trpc.cadastro.criarPessoa.mutate({
      cpf: f.cpf, nome: f.nome.trim(),
      nomeSocial: f.nomeSocial.trim() || null,
      dataNascimento: f.dataNascimento,
      sexo: f.sexo, racaCor: f.racaCor, grauInstrucao: f.grauInstrucao,
      nomeMae: f.nomeMae.trim(),
      nisPisPasep: f.nisPisPasep.replace(/\D/g, "") || null,
    }),
    onSuccess: aoCriar,
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  const txt = (k: keyof typeof f) => ({
    value: String(f[k]),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((v) => ({ ...v, [k]: e.target.value })),
  });
  const num = (k: keyof typeof f) => ({
    value: String(f[k]),
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
      setF((v) => ({ ...v, [k]: Number(e.target.value) })),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="Nova pessoa"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal">
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Nova pessoa</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            Raça e cor, grau de instrução e nome da mãe são obrigatórios no S-2200.
            Faltando, a rejeição vem em lote na virada.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); criar.mutate(); }}>
          <div className="grid gap-3.5 sm:grid-cols-2">
            <label className="block">
              <span className="eop-rotulo">CPF</span>
              <input className="campo mt-1.5 num" inputMode="numeric" required {...txt("cpf")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Data de nascimento</span>
              <input className="campo mt-1.5" type="date" required {...txt("dataNascimento")} />
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Nome completo</span>
              <input className="campo mt-1.5" required {...txt("nome")} />
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Nome social</span>
              <input className="campo mt-1.5" {...txt("nomeSocial")} />
              <span className="carimbo mt-1 block">
                se preenchido, é ele que aparece em toda a tela
              </span>
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Nome da mãe</span>
              <input className="campo mt-1.5" required {...txt("nomeMae")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Sexo</span>
              <select className="campo mt-1.5" {...txt("sexo")}>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
              </select>
            </label>
            <label className="block">
              <span className="eop-rotulo">NIS, PIS ou PASEP</span>
              <input className="campo mt-1.5 num" inputMode="numeric" {...txt("nisPisPasep")} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Raça e cor</span>
              <select className="campo mt-1.5" {...num("racaCor")}>
                {RACA_COR.map((r) => <option key={r.codigo} value={r.codigo}>{r.nome}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="eop-rotulo">Grau de instrução</span>
              <select className="campo mt-1.5" {...num("grauInstrucao")}>
                {GRAU_INSTRUCAO.map((g) => <option key={g.codigo} value={g.codigo}>{g.nome}</option>)}
              </select>
            </label>
          </div>

          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={criar.isPending}>
              {criar.isPending ? "Salvando" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
